/**
 * wallet-core/secure-storage.ts
 *
 * Android/iOS: Keystore / Keychain only (fail-closed).
 * Web: IndexedDB + localStorage mirror so wallets survive reload / port changes
 * within the same origin. (Note: localhost:5173 and :5175 are DIFFERENT origins.)
 */

import { Capacitor } from '@capacitor/core'
import { SecureStoragePlugin } from '@capacitor-community/secure-storage-plugin'
import { createStore, del, get, set } from 'idb-keyval'

const DB_NAME = 'properwallet'
const STORE_NAME = 'wallet'
const idbStore = createStore(DB_NAME, STORE_NAME)

const LS_PREFIX = 'pw_ss_'
function lsKey(key: string): string {
  return `${LS_PREFIX}${key}`
}

function expectNativeSecureStorage(): boolean {
  if (typeof window === 'undefined') return false
  const w = window as unknown as { __CAPACITOR_PLATFORM__?: string }
  if (w.__CAPACITOR_PLATFORM__ === 'android' || w.__CAPACITOR_PLATFORM__ === 'ios') return true
  try {
    const platform = Capacitor.getPlatform()
    if (platform === 'android' || platform === 'ios') return true
  } catch {
    /* ignore */
  }
  return Capacitor.isNativePlatform()
}

export async function secureGet(key: string): Promise<string | null> {
  if (expectNativeSecureStorage()) {
    try {
      const { value } = await SecureStoragePlugin.get({ key })
      return value
    } catch (e) {
      console.warn('[wallet-core/secure-storage] native get failed (fail-closed)', { key, error: e })
      return null
    }
  }

  try {
    const v = await get<unknown>(key, idbStore)
    if (v !== undefined && v !== null && typeof v === 'string') return v
  } catch (e) {
    console.warn('[wallet-core/secure-storage] idb get failed', { key, error: e })
  }

  try {
    const mirrored = localStorage.getItem(lsKey(key))
    if (mirrored !== null) {
      try {
        await set(key, mirrored, idbStore)
      } catch {
        /* ignore */
      }
      return mirrored
    }
  } catch {
    /* ignore */
  }

  // Also accept bare key (storage.ts used to write wallet-state without prefix)
  try {
    const bare = localStorage.getItem(key)
    if (bare !== null) {
      try {
        await set(key, bare, idbStore)
        localStorage.setItem(lsKey(key), bare)
      } catch {
        /* ignore */
      }
      return bare
    }
  } catch {
    /* ignore */
  }

  return null
}

export async function secureSet(key: string, value: string): Promise<void> {
  if (expectNativeSecureStorage()) {
    try {
      await SecureStoragePlugin.set({ key, value })
    } catch (e) {
      console.error('[wallet-core/secure-storage] native set failed (fail-closed)', { key, error: e })
      throw new Error(
        `Secure storage unavailable on native platform — refusing to store secret "${key}" in web storage`,
      )
    }
    return
  }

  let idbOk = false
  try {
    await set(key, value, idbStore)
    idbOk = true
  } catch (e) {
    console.warn('[wallet-core/secure-storage] idb set failed, using localStorage', { key, error: e })
  }

  // Always mirror on web so F5 / new tab keeps the wallet
  try {
    localStorage.setItem(lsKey(key), value)
    localStorage.setItem(key, value)
  } catch (e) {
    if (!idbOk) {
      console.error('[wallet-core/secure-storage] both idb and localStorage failed', { key, error: e })
      throw new Error(`secureSet failed for key "${key}"`)
    }
  }
}

export async function secureRemove(key: string): Promise<void> {
  if (expectNativeSecureStorage()) {
    try {
      await SecureStoragePlugin.remove({ key })
    } catch (e) {
      console.warn('[wallet-core/secure-storage] native remove failed', { key, error: e })
    }
    return
  }
  try {
    await del(key, idbStore)
  } catch (e) {
    console.warn('[wallet-core/secure-storage] idb remove failed', { key, error: e })
  }
  try {
    localStorage.removeItem(lsKey(key))
    localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

export const legacyWalletIdbStore = idbStore
